import { useFragment } from "react-relay"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { SearchItem } from "@/components/ui/search"
import { joinStrings } from "@/lib/joinString"
import { useIcdDiagnosisQuery$data } from "@/modules/patients/hooks/__generated__/useIcdDiagnosisQuery.graphql"
import {
  IcdDiagnosisFragment$data,
  IcdDiagnosisFragment$key,
} from "@/modules/patients/services/__generated__/IcdDiagnosisFragment.graphql"
import { IcdDiagnosisFragment } from "@/modules/patients/services/IcdDiagnosisFragment"

export type IcdDiagnosisSearch = useIcdDiagnosisQuery$data["icdDiagnosisSearch"]
export type HighlightsSearchIcdObject = IcdDiagnosisSearch["commons"][0]["highlights"]

interface SearchOptionProps {
  highlights: HighlightsSearchIcdObject
  option: IcdDiagnosisFragment$key
  onSelect: (value: IcdDiagnosisFragment$data) => void
}

const getHighlightedValue = (highlights?: HighlightsSearchIcdObject, value?: string, defaultValue?: string | null) => {
  return highlights?.find((h) => h.key === value)?.value ?? defaultValue
}

const formatHightlightValue = (searchOption: IcdDiagnosisFragment$data, highlights?: HighlightsSearchIcdObject) => {
  return joinStrings(
    [
      getHighlightedValue(highlights, "diagnosisCode", searchOption.diagnosisCode),
      getHighlightedValue(highlights, "text", searchOption.text),
    ],
    " - ",
  )
}

export const IcdCodeSearchOption = (props: SearchOptionProps) => {
  const { highlights, onSelect } = props
  const option = useFragment(IcdDiagnosisFragment, props.option)

  return (
    <SearchItem
      key={option.diagnosisCode}
      className="overflow-hidden rounded-md py-1.5"
      data-testid="icd10-code-item"
      onSelect={() => onSelect(option)}
      value={option.diagnosisCode}>
      <div className="grid" data-testid={option.diagnosisCode}>
        <span className="text-body-sm truncate">
          <PotentiallyUnsafeInnerHtml value={formatHightlightValue(option, highlights)} />
        </span>
        <span className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]">
          <PotentiallyUnsafeInnerHtml
            className="line-clamp-3"
            value={getHighlightedValue(highlights, "synonyms") ?? ""}
          />
        </span>
      </div>
    </SearchItem>
  )
}
